import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  ScrollView,
  TouchableOpacity,
  Modal,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, useFocusEffect } from 'expo-router';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { leerKycDocs, urlDeDocumento } from '../utils/kycDocs';

/**
 * "Mi vehículo" en el perfil del chofer — foto, color y placa, como en Uber
 * e InDriver.
 *
 * POR QUÉ NO EXISTÍA
 * El embudo de KYC SÍ pide todo esto: marca, modelo, color y placa
 * (kyc-documents.tsx) y cinco fotos del carro (frente, lateral derecho,
 * lateral izquierdo, trasera con la placa legible, e interior), que quedan
 * guardadas en `users/{uid}.kycDocs`. Pero el perfil no mostraba NINGUNO de
 * esos datos: el chofer subía las cinco fotos y su vehículo desaparecía de su
 * vista. No podía comprobar qué había mandado, ni si estaba correcto.
 *
 * El pasajero sí ve marca/color/placa cuando le llega una oferta
 * (RideOfferCard), así que el dato existía y viajaba — lo que faltaba era el
 * espejo del propio chofer.
 *
 * Se pinta sola: si el usuario no tiene datos de vehículo, no renderiza nada.
 */

/**
 * SIN LISTA FIJA DE FOTOS — A PROPÓSITO.
 *
 * Las dos versiones anteriores de esta tarjeta fallaron por lo mismo: escribir
 * a mano las claves que guarda el KYC. Primero se leyó el título crudo
 * ('1. Frente del Vehículo') cuando se guarda el slug; después se corrigió el
 * slug pero se inventaron los del MOTOR — se puso
 * '2_placa_trasera_legible' cuando el KYC guarda '2_lado_de_la_placa', y
 * faltaban las tomas de asientos y de seguros de puertas.
 *
 * Cualquier lista escrita aquí se desincroniza en cuanto alguien cambia un
 * título en `kyc-documents.tsx`. Así que ya no hay lista: se muestran TODAS
 * las fotos que el usuario tenga guardadas, salvo las de identidad, que no
 * pintan nada en una tarjeta de vehículo (y no queremos enseñar la cédula).
 */
/*
 * ⚠️ `seguro` A SECAS, NO `seguro_del`.
 *
 * El documento real del fundador guarda la póliza como `seguro_vigente`, y
 * `seguro_del` no la caza: su seguro se colaba en la tira de fotos del carro.
 * Medido en Firestore el 11-sep-2026 — sus claves son `1_frente_del_vehiculo`,
 * `3_lateral_izquierdo`, `4_parte_trasera`, `5_interior_del_vehiculo`,
 * `cedula_frente`, `cedula_dorso`, `licencia_de_conducir`,
 * `matricula_del_vehiculo` y `seguro_vigente`.
 *
 * En su propio perfil solo era feo. Pero esta misma lista es la que decide qué
 * es "foto del vehículo", y el día que esa tira se le enseñe al PASAJERO, un
 * `seguro_del` que no caza deja la póliza —con su nombre y su número— delante
 * de un desconocido. Se cierra ahora, antes de que exista ese día.
 */
/**
 * ══════════════════════════════════════════════════════════════════════════
 * EL VISOR QUE NO EXISTIA — LA PLACA NO SE PODIA VER NUNCA
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La tira de abajo pinta las fotos en cajas de 130x95 con `resizeMode="cover"`:
 * la imagen se agranda hasta tapar la caja y lo que sobra se recorta. Una foto
 * de carro es casi siempre 4:3 o mas alta (se toma con el telefono de pie), y
 * la caja es ~1.37:1, asi que lo recortado es arriba y abajo. Abajo es donde
 * esta la placa.
 *
 * Y el `<Image>` no estaba envuelto en nada pulsable: no habia visor en todo el
 * componente. O sea que la placa no se podia leer NI tocando. El chofer sube
 * cinco fotos de su carro y no tiene forma de comprobar que la de la placa
 * salio bien — que es justo para lo que las sube.
 *
 * La miniatura se queda con `cover` a proposito: una tira de recuadros del
 * mismo tamano se lee de un golpe, y con `contain` las fotos verticales dejan
 * franjas y la tira queda despareja. Lo que se arregla es que ahora se puede
 * ABRIR: aqui dentro va `contain`, sin recortar nada.
 *
 * Detalles que no son adorno:
 *  · `onRequestClose` — sin eso el boton Atras de Android no cierra el modal y
 *    se come la navegacion del perfil. Ya paso antes en este proyecto.
 *  · estado de carga y de error — la URI viene de Storage con token; si falla,
 *    sin esto el visor se queda en un cuadro negro sin decir nada.
 */
function VisorDeFoto({ uri, onCerrar }: { uri: string | null; onCerrar: () => void }) {
  const { width, height } = useWindowDimensions();
  const [cargando, setCargando] = React.useState(true);
  const [fallo, setFallo] = React.useState(false);

  // Cada foto que se abre empieza de cero: sin esto, abrir una segunda foto
  // heredaba el "fallo" de la primera y se veia el error con la imagen buena.
  React.useEffect(() => {
    if (uri) {
      setCargando(true);
      setFallo(false);
    }
  }, [uri]);

  return (
    <Modal visible={!!uri} transparent animationType="fade" onRequestClose={onCerrar}>
      <TouchableOpacity style={styles.visorFondo} activeOpacity={1} onPress={onCerrar}>
        <TouchableOpacity
          style={styles.visorCerrar}
          onPress={onCerrar}
          accessibilityRole="button"
          accessibilityLabel="Cerrar la foto"
        >
          <Ionicons name="close" size={28} color="#FFFFFF" />
        </TouchableOpacity>

        {uri ? (
          <Image
            source={{ uri }}
            style={{ width: width, height: height * 0.75 }}
            resizeMode="contain"
            onLoadStart={() => setCargando(true)}
            onLoad={() => setCargando(false)}
            onError={() => {
              setCargando(false);
              setFallo(true);
            }}
            accessibilityLabel="Foto de tu vehículo a pantalla completa"
          />
        ) : null}

        {cargando && !fallo ? (
          <ActivityIndicator size="large" color="#FFFFFF" style={styles.visorCargando} />
        ) : null}

        {fallo ? (
          <Text style={styles.visorError}>
            No se pudo cargar la foto. Revisa tu conexión e inténtalo otra vez.
          </Text>
        ) : (
          <Text style={styles.visorPie}>Toca la foto para cerrar</Text>
        )}
      </TouchableOpacity>
    </Modal>
  );
}

const CLAVES_QUE_NO_SON_DEL_VEHICULO =
  /cedula|c_dula|selfie|licencia|conducta|seguro|matricula|matr_cula|poliza|p_liza/i;


export default function TarjetaMiVehiculo() {
  // Que foto del vehiculo se esta viendo a pantalla completa. `null` = ninguna.
  const [fotoAbierta, setFotoAbierta] = React.useState<string | null>(null);
  const colors = useThemeColors();
  const router = useRouter();

  const uid = useAppStore((s) => s.userFirebaseUid);
  const marca = useAppStore((s) => s.vehicleBrand);
  const modelo = useAppStore((s) => s.vehicleModel);
  const color = useAppStore((s) => s.vehicleColor);
  const placa = useAppStore((s) => s.licensePlate);

  const [fotos, setFotos] = React.useState<string[]>([]);
  // El tipo de vehiculo no vive en el store; se toma del perfil que ya se lee
  // aqui abajo. Sirve para que el formulario hable de su motor o de su carro.
  const [tipoDeVehiculo, setTipoDeVehiculo] = React.useState<string>('');
  // El seguro es el unico papel del vehiculo que CADUCA, y el unico que el
  // chofer puede renovar solo. Aqui solo hacen falta dos datos para decidir que
  // se le ensena: cuando vence y si esta bloqueado por vencido.
  const [seguro, setSeguro] = React.useState<{ venceMs: number | null; bloqueado: boolean; enRevision: boolean }>(
    { venceMs: null, bloqueado: false, enRevision: false }
  );

  // `useFocusEffect` y no `useEffect([uid])`: la pestaña de Perfil NO se
  // desmonta nunca, así que con el efecto de montaje el chofer subía sus fotos,
  // volvía al perfil y seguía viendo lo mismo hasta cerrar y abrir la app.
  // Ahora se relee cada vez que la pantalla vuelve a estar delante.
  useFocusEffect(
    React.useCallback(() => {
      if (!uid) return;
      let vivo = true;
      (async () => {
        try {
          const perfil = await firestoreService.getUserProfile(uid);
          if (!vivo) return;
          setTipoDeVehiculo(String((perfil as any)?.vehicleType ?? ''));
          const v: any = (perfil as any)?.seguroVenceEl;
          setSeguro({
            venceMs: typeof v?.toMillis === 'function'
              ? v.toMillis()
              : typeof v?.seconds === 'number' ? v.seconds * 1000 : null,
            bloqueado: (perfil as any)?.seguroVencidoBloqueo === true,
            enRevision: !!(perfil as any)?.seguroPendienteUrl,
          });
          const docs = leerKycDocs(perfil);
          const urls = Object.entries(docs)
            .filter(([clave]) => !CLAVES_QUE_NO_SON_DEL_VEHICULO.test(clave))
            .sort(([a], [b]) => a.localeCompare(b)) // 1_, 2_, 3_… en orden
            .map(([, doc]) => urlDeDocumento(doc))
            .filter((u): u is string => !!u);
          setFotos(urls);
        } catch (e: any) {
          // Sin fotos la tarjeta sigue mostrando color y placa; no se rompe nada.
          console.warn('[TarjetaMiVehiculo] no se pudieron leer las fotos:', e?.message);
        }
      })();
      return () => { vivo = false; };
    }, [uid])
  );

  const descripcion = [color, marca, modelo].filter(Boolean).join(' ');

  // ANTES: `if (!hayAlgo) return null` — si el chofer no había llenado nada,
  // la tarjeta DESAPARECÍA por completo y él no tenía forma de saber que le
  // faltaba algo ni dónde ponerlo. Ahora siempre se pinta y guía.

  return (
    <View style={[styles.caja, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.cabecera}>
        <Ionicons name="car-sport" size={22} color={colors.primary} />
        <Text style={[styles.titulo, { color: colors.text }]}>Mi vehículo</Text>
        {/* ══════════════════════════════════════════════════════════════
            «EDITAR» LLEVABA AL EMBUDO ENTERO, NO AL FORMULARIO
            ══════════════════════════════════════════════════════════════
            Esto hacia `router.push('/kyc-documents')` A SECAS. Sin el parametro
            `focoVehiculo`, esa pantalla se abre como el paso del ALTA: arranca
            con dos avisos y los documentos de identidad, no destapa el boton de
            guardar el vehiculo, y su salida empuja hacia /kyc-welcome. El
            chofer que solo venia a corregir su placa acababa en el embudo de
            registro — «lo lleva a donde esta el que esta registrado» y de ahi
            a la pantalla que le pide elegir rol otra vez.
            Es el mismo enlace que ya usaba bien `driver-subscription.tsx:331`:
            va derecho al formulario, desplazado hasta el, y vuelve con
            `router.back()`. */}
        <TouchableOpacity
          onPress={() =>
            router.push({
              pathname: '/kyc-documents',
              params: { vehicle: tipoDeVehiculo, focoVehiculo: '1' },
            } as any)
          }
          accessibilityRole="button"
          accessibilityLabel="Editar los datos de mi vehículo"
        >
          <Text style={[styles.editar, { color: colors.primary }]}>Editar</Text>
        </TouchableOpacity>
      </View>

      {descripcion ? (
        <Text style={[styles.descripcion, { color: colors.text }]}>{descripcion}</Text>
      ) : (
        <Text style={[styles.sinFotos, { color: colors.textSecondary, marginTop: 0 }]}>
          Aún no has registrado marca, modelo ni color. Toca “Editar” y guarda con el botón
          “Validar Documentos y Continuar” al final.
        </Text>
      )}

      {!!placa && (
        <View style={styles.placaMarco}>
          <Text style={styles.placaTexto}>{placa}</Text>
        </View>
      )}

      {/* ── CAMBIAR DE VEHÍCULO, AQUÍ ────────────────────────────────────
          Esta acción vivía arriba del todo en la pantalla de «Documentos
          Requeridos», pegada a la flecha de salir: el fundador lo dijo claro,
          «eso no puede ir ahí porque ya está puesta en el perfil, tiene que ir
          donde están los datos del vehículo registrado». Tenía razón — un
          botón que REESCRIBE tu tipo de vehículo no puede estar donde uno va
          buscando la salida, y menos con la misma pinta que ella.
          Aquí, debajo de la marca, la placa y las fotos, se entiende lo que
          cambia porque se está viendo. */}
      {/* ══════════════════════════════════════════════════════════════════
          A «MIS VEHICULOS», NO AL EMBUDO DEL ALTA
          ══════════════════════════════════════════════════════════════════
          Esto apuntaba a /kyc-welcome, que es el selector del REGISTRO: elegir
          ahi un vehiculo llama a `setDriverRole` y reescribe el ROL del chofer
          por Cloud Function. Para cambiar de carro eso es una escopeta.
          Ahora va a la lista de verdad, donde cada vehiculo tiene su ficha, su
          estado y su boton de borrar. */}
      <TouchableOpacity
        onPress={() => router.push('/mis-vehiculos' as any)}
        style={[styles.cambiarVehiculo, { borderColor: colors.border }]}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel="Ver mis vehículos, agregar otro o cambiar con cuál conduzco"
      >
        <Ionicons name="swap-horizontal" size={16} color={colors.textSecondary} />
        <Text style={[styles.cambiarVehiculoTexto, { color: colors.textSecondary }]}>
          Mis vehículos · agregar o cambiar
        </Text>
      </TouchableOpacity>
      {/* ══════════════════════════════════════════════════════════════════
          EL SEGURO, AQUI
          ══════════════════════════════════════════════════════════════════
          Es el unico documento del vehiculo que caduca, y el unico que el
          chofer puede renovar sin repetir la verificacion entera (decision del
          fundador, 2-oct-2026). Esta fila va aqui porque es donde el chofer
          viene a buscar los papeles de su carro — no enterrada en ajustes.
          El estado se ensena EN la fila: «vence en 12 dias» le dice que hacer
          sin tener que entrar a averiguarlo. */}
      <TouchableOpacity
        onPress={() => router.push('/seguro' as any)}
        style={[styles.cambiarVehiculo, {
          borderColor: seguro.bloqueado ? '#D32F2F' : colors.border,
          marginTop: 8,
        }]}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel="Mi seguro: ver el vencimiento y enviar una póliza nueva"
      >
        <Ionicons
          name={seguro.bloqueado ? 'alert-circle' : 'shield-checkmark'}
          size={16}
          color={seguro.bloqueado ? '#D32F2F' : colors.textSecondary}
        />
        <Text style={[styles.cambiarVehiculoTexto, {
          color: seguro.bloqueado ? '#D32F2F' : colors.textSecondary,
        }]}>
          {seguro.bloqueado
            ? 'Mi seguro · VENCIDO, envía tu póliza nueva'
            : seguro.enRevision
              ? 'Mi seguro · estamos revisando tu póliza'
              : seguro.venceMs == null
                ? 'Mi seguro · falta la fecha de vencimiento'
                : (() => {
                    const dias = Math.ceil((seguro.venceMs - Date.now()) / 86400000);
                    return dias <= 30
                      ? `Mi seguro · vence en ${dias <= 0 ? 'hoy' : dias + ' día' + (dias === 1 ? '' : 's')}`
                      : 'Mi seguro · al día';
                  })()}
        </Text>
      </TouchableOpacity>
      <Text style={[styles.notaUnVehiculo, { color: colors.textSecondary }]}>
        Puedes tener varios vehículos registrados y elegir con cuál conduces. El que esté activo es el
        que verá tu pasajero cuando llegues a recogerlo.
      </Text>

      {fotos.length > 0 ? (
        <>
          {/* Se dice que se pueden abrir. Un recuadro que no anuncia nada no se
              toca: el chofer no sabia que tenia la foto completa a un toque. */}
          <Text style={[styles.pistaDeToque, { color: colors.textSecondary }]}>
            Toca una foto para verla completa y comprobar que la placa se lee.
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8 }}>
            {fotos.map((uri, i) => (
              <TouchableOpacity
                key={uri + i}
                onPress={() => setFotoAbierta(uri)}
                activeOpacity={0.8}
                accessibilityRole="button"
                accessibilityLabel={`Ver completa la foto ${i + 1} de tu vehículo`}
              >
                <Image source={{ uri }} style={styles.foto} resizeMode="cover" />
                {/* La lupa avisa de que la miniatura esta recortada y que hay
                    mas foto detras. */}
                <View style={styles.lupa}>
                  <Ionicons name="expand" size={13} color="#FFFFFF" />
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <VisorDeFoto uri={fotoAbierta} onCerrar={() => setFotoAbierta(null)} />
        </>
      ) : (
        <Text style={[styles.sinFotos, { color: colors.textSecondary }]}>
          Aún no has subido las fotos de tu vehículo. Los pasajeros confían más cuando ven el
          carro que los va a recoger.
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  caja: { borderRadius: 16, borderWidth: 1, padding: 16, marginBottom: 15 },
  cabecera: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  titulo: { fontSize: 16, fontWeight: 'bold', marginLeft: 8, flex: 1 },
  editar: { fontSize: 13, fontWeight: '600' },
  descripcion: { fontSize: 15, fontWeight: '600', textTransform: 'capitalize' },
  // Marco tipo matrícula: es como lo muestran Uber e InDriver, y el pasajero
  // lo reconoce de un vistazo al comparar con el carro que tiene delante.
  placaMarco: {
    alignSelf: 'flex-start',
    marginTop: 8,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#1F2937',
    backgroundColor: '#FFFFFF',
  },
  placaTexto: { fontSize: 18, fontWeight: '900', letterSpacing: 2, color: '#111827' },
  foto: { width: 130, height: 95, borderRadius: 10, marginRight: 10, backgroundColor: '#E5E7EB' },
  pistaDeToque: { fontSize: 11.5, lineHeight: 16, marginTop: 12 },
  cambiarVehiculo: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 7,
    marginTop: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
  },
  cambiarVehiculoTexto: { fontSize: 12.5, fontWeight: '600' },
  notaUnVehiculo: { fontSize: 11.5, lineHeight: 16, marginTop: 8 },
  lupa: {
    position: 'absolute',
    right: 16,
    bottom: 6,
    backgroundColor: 'rgba(17,24,39,0.62)',
    borderRadius: 11,
    paddingHorizontal: 5,
    paddingVertical: 4,
  },
  visorFondo: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.93)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  visorCerrar: {
    position: 'absolute',
    top: 44,
    right: 18,
    zIndex: 2,
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderRadius: 22,
    padding: 9,
  },
  visorCargando: { position: 'absolute' },
  visorPie: {
    position: 'absolute',
    bottom: 42,
    color: 'rgba(255,255,255,0.72)',
    fontSize: 12.5,
  },
  visorError: {
    position: 'absolute',
    bottom: 42,
    color: '#FCA5A5',
    fontSize: 13,
    textAlign: 'center',
    paddingHorizontal: 32,
    lineHeight: 18,
  },
  sinFotos: { fontSize: 12, lineHeight: 17, marginTop: 10 },
});
